import React from 'react';
import {Flex, Text, Icon, Button, Alert} from '@spothero/ui';
import IconCheck from '@spothero/icons/checkmark';
import ReservationUtils from 'utils/reservation-utils';
import {PROPTYPE_RESERVATION} from '@/proptypes/index';
import useMobileBreakPoint from 'hooks/use-mobile-breakpoint';
import ScanCode from '../scan-code';
import PropTypes from 'prop-types';
import NotificationBox from '../notification-box';
import StallDisplay from '../stall-display';
import CommuterRateInfo from '../commuter-rate-info';
import FilledInfoCircle from '@spothero/icons/info-circle-filled';

const formatLicenseString = licensePlate => {
    if (licensePlate.length === 6) {
        return `${licensePlate.slice(0, 3)} ${licensePlate.slice(3)}`;
    }

    return licensePlate;
};

const Redemption = ({
    reservation,
    redemptionType,
    activeStatus,
    onInstructionsClick,
    onPrintClick,
    onTrackedClick,
}) => {
    const handlePrintClick = event => {
        onTrackedClick({
            clickTarget: 'Print This Page',
            isReservationChange: false,
        });

        onPrintClick(event);
    };
    const isDesktop = !useMobileBreakPoint('desktop');
    const isScanCodeRedemption =
        redemptionType ===
        ReservationUtils.PARKING_PASS_REDEMPTION_TYPE.SCAN_CODE;
    const isLicensePlateRedemption =
        redemptionType ===
        ReservationUtils.PARKING_PASS_REDEMPTION_TYPE.LICENSE_PLATE;
    const hasExternalId =
        reservation.displayId !== String(reservation.rentalId) &&
        reservation.displayId.length > 10;
    const {
        paymentStatus,
        entryTimestamp,
        facility: {allowUpdatingReservationAfterEntry},
    } = reservation;

    const renderRedemption = () => {
        if (isScanCodeRedemption) {
            return (
                <ScanCode
                    rentalId={reservation.rentalId}
                    barcodeType={reservation.facility.barcodeType}
                    isActive={
                        activeStatus !==
                        ReservationUtils.ACTIVE_STATUS.NOT_STARTED
                    }
                />
            );
        } else if (isLicensePlateRedemption) {
            return (
                <>
                    <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="text.secondary.light"
                    >
                        License Plate
                    </Text>
                    <Text fontWeight="bold" fontSize="2xl">
                        {formatLicenseString(
                            reservation.vehicle?.licensePlateValue || ''
                        )}
                    </Text>
                </>
            );
        } else {
            return (
                <>
                    <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="text.secondary.light"
                    >
                        Rental ID
                    </Text>
                    <Text
                        fontWeight={hasExternalId ? 'semibold' : 'bold'}
                        fontSize={hasExternalId ? 'base' : '2xl'}
                    >
                        {reservation.displayId}
                    </Text>
                </>
            );
        }
    };

    return (
        <Flex
            backgroundColor="white"
            paddingTop={{base: 4, tablet: 6, desktop: 12}}
            paddingBottom={{base: 6, desktop: 12}}
            borderBottomLeftRadius={{base: '0', desktop: 'md'}}
            borderTopLeftRadius="md"
            borderTopRightRadius={{base: 'md', desktop: '0'}}
            width="100%"
            flex={{base: null, desktop: 1}}
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            sx={{
                '@media print': {
                    borderRightWidth: '1px',
                    borderTopRightRadius: '0',
                    borderBottomRightRadius: '0',
                    borderColor: 'gray.100',
                    flex: 1,
                },
            }}
        >
            {paymentStatus === ReservationUtils.PAYMENT_STATUSES.REFUND ? (
                <Flex
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    paddingY={{base: 6, tablet: 0}}
                >
                    {reservation.reservationType ===
                        ReservationUtils.RESERVATION_TYPES.MONTHLY && (
                        <Flex
                            borderRadius="md"
                            boxShadow="lg"
                            flexDirection="column"
                            alignItems="center"
                            paddingX={isScanCodeRedemption ? 0 : 8}
                            paddingY={isScanCodeRedemption ? 0 : 4}
                            backgroundColor="white"
                            marginBottom={4}
                        >
                            {renderRedemption()}
                        </Flex>
                    )}
                    <Flex>
                        <Icon
                            width={4}
                            height="auto"
                            as={IconCheck}
                            color="success"
                            marginRight={1.5}
                        />
                        <Text
                            fontSize="lg"
                            fontWeight="semibold"
                            color="success"
                        >
                            Reservation Refunded
                        </Text>
                    </Flex>
                    <Flex
                        maxWidth="60%"
                        justifyContent="center"
                        flexDirection="column"
                    >
                        {reservation.partner ? (
                            <Text
                                fontSize="sm"
                                textAlign="center"
                                marginTop={3}
                                color="text.secondary.light"
                            >
                                Refunds may take up to 21 business days to
                                appear on payment accounts.
                            </Text>
                        ) : (
                            <>
                                <Text
                                    fontSize="sm"
                                    textAlign="center"
                                    marginBottom={2}
                                    marginTop={3}
                                    color="text.secondary.light"
                                >
                                    Refunds to SpotHero credit are issued
                                    immediately.
                                </Text>
                                <Text
                                    fontSize="sm"
                                    textAlign="center"
                                    color="text.secondary.light"
                                >
                                    Refunds to other payment methods may take
                                    5–10 business days to appear on payment
                                    accounts.
                                </Text>
                            </>
                        )}
                    </Flex>
                </Flex>
            ) : (
                <Flex
                    marginTop={{base: -9, tablet: 0}}
                    flexDirection="column"
                    alignItems="center"
                    sx={{
                        '@media print': {
                            marginTop: 0,
                        },
                    }}
                >
                    <Flex
                        borderRadius="md"
                        boxShadow="lg"
                        flexDirection="column"
                        alignItems="center"
                        paddingX={isScanCodeRedemption ? 0 : 8}
                        paddingY={isScanCodeRedemption ? 0 : 4}
                        backgroundColor="white"
                    >
                        {renderRedemption()}
                    </Flex>
                    <Text
                        sx={{
                            '@media print': {
                                display: 'none',
                            },
                        }}
                        paddingTop={4}
                        fontWeight="bold"
                        fontSize="lg"
                        color={
                            activeStatus ===
                            ReservationUtils.ACTIVE_STATUS.ENDED
                                ? 'text.primary.light'
                                : 'text.secondary.light'
                        }
                    >
                        {activeStatus}
                    </Text>
                </Flex>
            )}
            {isDesktop &&
            reservation.paymentStatus !==
                ReservationUtils.PAYMENT_STATUSES.REFUND &&
            activeStatus !== ReservationUtils.ACTIVE_STATUS.ENDED ? (
                <Flex
                    sx={{
                        '@media print': {
                            display: 'none',
                        },
                    }}
                    marginTop={3}
                    alignItems="center"
                >
                    <Text
                        whiteSpace="pre"
                        color="text.secondary.light"
                        lineHeight="1.4"
                    >
                        {isScanCodeRedemption
                            ? 'Scan from a mobile device or '
                            : 'Use your mobile device or '}
                    </Text>
                    <Button
                        textTransform="none"
                        fontFamily="body"
                        variant="tertiary"
                        onClick={handlePrintClick}
                    >
                        print this page
                    </Button>
                </Flex>
            ) : null}
            <StallDisplay stallName={reservation.stallName} />
            <Flex flexDirection="column" maxWidth="21.5rem" marginX={4}>
                <CommuterRateInfo
                    commuterRateDetails={reservation.commuterRateDetails}
                />
                <NotificationBox
                    vehicleRequired={reservation.vehicleRequired}
                    vehicle={reservation.vehicle}
                    activeStatus={activeStatus}
                    onInstructionsClick={onInstructionsClick}
                    paymentStatus={paymentStatus}
                    phoneNumber={reservation.phoneNumber}
                    phoneNumberRequired={
                        reservation.facility.phoneNumberRequired ||
                        reservation.reservationType ===
                            ReservationUtils.RESERVATION_TYPES.MONTHLY
                    }
                    partner={reservation.partner}
                />
                {!allowUpdatingReservationAfterEntry && !entryTimestamp && (
                    <Alert
                        status="neutral"
                        description={
                            <>
                                <strong>
                                    Time & vehicle changes are not allowed at
                                    this facility after entry.
                                </strong>{' '}
                                You will not be refunded for leaving early and
                                you may be charged extra if you leave late.
                            </>
                        }
                        showIcon
                        icon={FilledInfoCircle}
                        marginTop={4}
                    />
                )}
            </Flex>
        </Flex>
    );
};

Redemption.propTypes = {
    reservation: PROPTYPE_RESERVATION,
    redemptionType: PropTypes.string.isRequired,
    activeStatus: PropTypes.oneOf(
        Object.values(ReservationUtils.ACTIVE_STATUS)
    ),
    onInstructionsClick: PropTypes.func.isRequired,
    onPrintClick: PropTypes.func.isRequired,
    onTrackedClick: PropTypes.func.isRequired,
};

export default Redemption;
